import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BiSend } from 'react-icons/bi';
import { FiTrash2 } from 'react-icons/fi';

import { Avatar, Modal } from '@/components';
import { Accordion } from '@/components/Accordion/Accordion';
import { ButtonOutlinedSmall, ButtonSmall } from '@/components/Button';
import { guestInvitationSchema } from '@/components/GuestInvitationModal/guestInvitation.schema';
import type { InviteCrewMember } from '@/components/GuestInvitationModal/guestInvitation.schema';
import { ContactInput, Input, Select } from '@/components/Inputs';
import { isOwnerGroupRole } from '@/utils/roles';

import { formatRole, shortEventLine } from './WhosOnBoardWidget.constants';
import type { EventOption, Person } from './WhosOnBoardWidget.constants';
import styles from './WhosOnBoardWidget.module.scss';

type RoleFilter = 'all' | 'crew' | 'guests';

const isGuest = (person: Person): boolean => {
  const role = person.role.toLowerCase();
  return role.includes('guest') || isOwnerGroupRole(role);
};

interface ManageManifestModalProps {
  open: boolean;
  onClose: () => void;
  people: Person[];
  events: EventOption[];
  selectedEventId: string;
  currentUserId?: string;
  onSelectEvent: (id: string) => void;
  onRemovePerson: (id: string) => Promise<void> | void;
  onInviteGuest: (values: InviteCrewMember) => Promise<void> | void;
}

export const ManageManifestModal = ({
  open,
  onClose,
  people,
  events,
  selectedEventId,
  currentUserId,
  onSelectEvent,
  onRemovePerson,
  onInviteGuest,
}: ManageManifestModalProps) => {
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteCrewMember>({
    resolver: zodResolver(guestInvitationSchema),
    defaultValues: { name: '', contact: '' },
  });

  const submitInvite = async (values: InviteCrewMember) => {
    await onInviteGuest(values);
    reset();
  };

  const roleFilters: { key: RoleFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: people.length },
    { key: 'crew', label: 'Crew', count: people.filter((p) => !isGuest(p)).length },
    { key: 'guests', label: 'Guests', count: people.filter(isGuest).length },
  ];

  const manifestPeople = people.filter((p) => {
    if (roleFilter === 'crew') return !isGuest(p);
    if (roleFilter === 'guests') return isGuest(p);
    return true;
  });

  return (
    <Modal open={open} onClose={onClose} title="Manage Manifest">
      <div className={styles.manageBody}>
        {events.length > 0 && (
          <Select
            label="Event"
            value={selectedEventId}
            onChange={(value: string) => onSelectEvent(value)}
            options={events.map((ev) => ({ label: shortEventLine(ev), value: ev.id }))}
          />
        )}

        <div className={styles.manifestFilters}>
          {roleFilters.map((f) => {
            const active = roleFilter === f.key;
            return (
              <button
                type="button"
                key={f.key}
                className={`${styles.manifestFilterChip} ${
                  active ? styles.manifestFilterChipActive : ''
                }`}
                onClick={() => setRoleFilter(f.key)}
                aria-pressed={active}
              >
                {f.label} <span className={styles.manifestFilterCount}>{f.count}</span>
              </button>
            );
          })}
        </div>

        <div className={styles.manageList}>
          {manifestPeople.length === 0 ? (
            <div className={styles.manageEmpty}>
              No participants match. Please try another filter.
            </div>
          ) : (
            manifestPeople.map((p) => (
              <div key={p.id} className={styles.manageRow}>
                <Avatar firstName={p.firstName} lastName={p.lastName} size={28} />
                <div className={styles.manageRowInfo}>
                  <span className={styles.manageRowName}>
                    {p.firstName} {p.lastName}
                  </span>
                  <span className={styles.manageRowRole}>{formatRole(p.role)}</span>
                </div>
                {p.id !== currentUserId && (
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label={`Remove ${p.firstName} ${p.lastName}`}
                    onClick={() => void onRemovePerson(p.id)}
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {roleFilter === 'guests' && (
          <Accordion title="Invite Guest">
            <div className={styles.manageFormBody}>
              <Input
                label="Guest Name"
                placeholder="Alex Morgan"
                error={errors.name?.message}
                {...register('name')}
                mandatory
              />
              <Controller
                control={control}
                name="contact"
                render={({ field }) => (
                  <ContactInput
                    label="Mobile or Email"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.contact?.message}
                    mandatory
                  />
                )}
              />
              <div className={styles.manageFormActions}>
                <ButtonSmall onClick={handleSubmit(submitInvite)} disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Send Invitation'} <BiSend size={16} />
                </ButtonSmall>
              </div>
            </div>
          </Accordion>
        )}

        <div className={styles.manageFooter}>
          <ButtonOutlinedSmall onClick={onClose}>Close</ButtonOutlinedSmall>
        </div>
      </div>
    </Modal>
  );
};
